import type {
  PracticeSession,
  PracticeMode,
  PracticeScope,
  PracticeTask,
  SupportLevel,
  TaskOutcome,
  PracticeSummary,
} from "./types.ts";

const SUPPORT: SupportLevel[] = ["none", "goal", "piece", "candidates", "answer"];
export function strongestSupport(a: SupportLevel, b: SupportLevel): SupportLevel {
  return SUPPORT[Math.max(SUPPORT.indexOf(a), SUPPORT.indexOf(b))];
}

export function newPracticeSession(
  id: string,
  slug: string,
  mode: PracticeMode,
  scope: Partial<PracticeScope> = {}
): PracticeSession {
  const now = Date.now();
  return {
    version: 1,
    id,
    slug,
    mode,
    startedAt: now,
    updatedAt: now,
    scope: {
      prefixSans: [],
      decisions: null,
      branchId: null,
      challenge: false,
      recent: false,
      ...scope,
    },
    outcomes: {},
    attempts: [],
    repairTasks: [],
    repairing: false,
    completedDecisions: 0,
  };
}

function emptyOutcome(task: PracticeTask): TaskOutcome {
  return {
    task,
    firstCorrect: null,
    firstPlayed: null,
    support: "none",
    answerRevealed: false,
    attempts: 0,
    correct: false,
    result: "studied",
  };
}

export function noteSupport(
  session: PracticeSession,
  task: PracticeTask,
  support: SupportLevel
): void {
  const outcome = (session.outcomes[task.id] ||= emptyOutcome(task));
  outcome.support = strongestSupport(outcome.support, support);
  outcome.answerRevealed ||= support === "answer";
  session.updatedAt = Date.now();
}

/** Wrong attempts survive undo, and a demonstration can never become independent recall. */
export function recordAttempt(
  session: PracticeSession,
  task: PracticeTask,
  played: string | null,
  correct: boolean,
  support: SupportLevel = "none"
): TaskOutcome {
  noteSupport(session, task, support);
  const outcome = session.outcomes[task.id];
  if (outcome.firstCorrect === null) {
    outcome.firstCorrect = correct;
    outcome.firstPlayed = played;
  }
  outcome.attempts += 1;
  outcome.correct = correct;
  outcome.result = !correct
    ? "missed"
    : !outcome.firstCorrect || outcome.answerRevealed
      ? "repaired"
      : outcome.support !== "none"
        ? "cued"
        : "independent";
  session.attempts.push({
    id: `${session.id}:${task.id}:${outcome.attempts}`,
    taskId: task.id,
    played,
    correct,
    support: outcome.support,
    at: Date.now(),
  });
  if (session.attempts.length > 500) session.attempts.shift();
  session.updatedAt = Date.now();
  return outcome;
}

export function practiceSummary(session: PracticeSession): PracticeSummary {
  const summary: PracticeSummary = { independent: 0, cued: 0, repaired: 0, missed: 0, total: 0 };
  for (const outcome of Object.values(session.outcomes)) {
    if (outcome.firstCorrect === null) continue;
    summary.total += 1;
    if (outcome.result !== "studied") summary[outcome.result] += 1;
  }
  return summary;
}
